// packages/shared/src/schemas/booking.schema.ts

import { z } from 'zod';

export const dayPartSchema = z.enum(['morning', 'afternoon', 'evening', 'any']);
export const channelSchema = z.enum(['phone', 'whatsapp', 'email']);

export const bookingCtaSchema = z.object({
  scanId: z.string().uuid(),
  treatmentId: z.string().uuid().optional(),
});

export const callbackRequestSchema = z.object({
  scanId: z.string().uuid().optional(),
  name: z.string().min(2).max(120),
  contact: z.string().min(4).max(160),
  channel: channelSchema,
  preferredDayPart: dayPartSchema,
  message: z.string().max(1000).optional(),
});

/**
 * A click is an INTENT. Only an observed appointment is a booking.
 *
 * `confirmedAt` is deliberately absent from every input schema — it is set by
 * the server, and only from a path that actually saw the appointment exist.
 */
export const bookingIntentSchema = z.object({
  scanId: z.string().uuid(),
  treatmentId: z.string().uuid().optional(),
  mode: z.enum(['deeplink', 'calendly', 'callback', 'native']),
});

/** Local wall-clock, resolved against the tenant's IANA timezone at query time,
 *  so 09:00 survives a DST change. */
export const availabilityRuleSchema = z.object({
  weekday: z.number().int().min(0).max(6),
  startTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM'),
  endTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM'),
  effectiveFrom: z.coerce.date().optional(),
  effectiveTo: z.coerce.date().optional(),
});

export const appointmentSchema = z.object({
  patientId: z.string().uuid(),
  practitionerId: z.string().uuid(),
  treatmentId: z.string().uuid(),
  // Always UTC on the wire. Rendering happens against the tenant timezone.
  startsAt: z.coerce.date(),
  notes: z.string().max(2000).optional(),
});

/**
 * Course booking — the differentiator. The recommender already says
 * "×6 every 14 days"; this books the whole series in one action.
 */
export const appointmentSeriesSchema = z.object({
  patientId: z.string().uuid(),
  practitionerId: z.string().uuid(),
  treatmentId: z.string().uuid(),
  firstSessionAt: z.coerce.date(),
  totalSessions: z.number().int().min(2).max(52),
  intervalDays: z.number().int().min(1).max(365),
});

export type CallbackRequestInput = z.infer<typeof callbackRequestSchema>;
export type BookingCtaInput = z.infer<typeof bookingCtaSchema>;
export type AppointmentInput = z.infer<typeof appointmentSchema>;
export type AppointmentSeriesInput = z.infer<typeof appointmentSeriesSchema>;
